/**
 * One component for every page under /about: the title, a lead, a contents
 * list, anchored sections of plain blocks, and a link to the next page. The
 * words live in web/src/docs/pages; this file only lays them out, so a hint
 * anywhere on the site can link to one paragraph by its section id. The two
 * live blocks read the build item and the page list at render time.
 *
 * Every page carries the same navigation: the list of all pages with the
 * current one marked, beside the text or above it, and the previous and next
 * page at the foot, so no page is a dead end and none is found only through
 * the footer.
 */
import { useEffect } from 'react';
import { api } from '../api.js';
import { Link } from '../components.js';
import { pageTitle, usePageTitle } from '../title.js';
import { useQuery } from '../useQuery.js';
import { docPages, holdsSentence, nextPage, previousPage } from './index.js';
import type { Block, DocPage as DocPageContent, Inline } from './types.js';

const Text = ({ parts }: { parts: readonly Inline[] }) => (
  <>
    {parts.map((part, index) =>
      typeof part === 'string' ? (
        <span key={index}>{part}</span>
      ) : part.link.startsWith('/') ? (
        <Link key={index} to={part.link}>
          {part.text}
        </Link>
      ) : (
        <a key={index} href={part.link} className="text-teal-800 underline decoration-teal-300 underline-offset-2 hover:decoration-teal-700" rel="noreferrer">
          {part.text}
        </a>
      ),
    )}
  </>
);

const Headline = () => {
  const build = useQuery('build', () => api.build());
  return (
    <p className="mt-3 max-w-3xl text-stone-800" data-testid="doc-headline">
      {holdsSentence(build.value ?? null)}
    </p>
  );
};

/** The landing page's table, built from the page list so it can never name a page that does not exist. */
const Pages = ({ current }: { current: string }) => (
  <table className="mt-3 w-full max-w-3xl text-sm" data-testid="doc-pages">
    <thead className="text-left text-stone-500">
      <tr>
        <th className="py-1 pr-3 font-normal">Page</th>
        <th className="py-1 pr-3 font-normal">For</th>
        <th className="py-1 font-normal">What it answers</th>
      </tr>
    </thead>
    <tbody>
      {docPages
        .filter((page) => page.slug !== current)
        .map((page) => (
          <tr key={page.slug} className="border-t border-stone-100">
            <td className="py-1 pr-3 align-top">
              <Link to={`/about/${page.slug}`}>{page.title}</Link>
            </td>
            <td className="py-1 pr-3 align-top text-stone-600">{page.audience}</td>
            <td className="py-1 align-top text-stone-800">{page.promise}</td>
          </tr>
        ))}
    </tbody>
  </table>
);

const BlockView = ({ block, current }: { block: Block; current: string }) => {
  switch (block.kind) {
    case 'paragraph':
      return (
        <p className="mt-3 max-w-3xl text-stone-800">
          <Text parts={block.text} />
        </p>
      );
    case 'note':
      return (
        <p className="mt-3 max-w-3xl border-l-2 border-stone-300 pl-3 text-sm text-stone-600">
          <Text parts={block.text} />
        </p>
      );
    case 'list':
      return (
        <ul className="mt-3 max-w-3xl list-disc space-y-1 pl-5 text-stone-800">
          {block.items.map((item, index) => (
            <li key={index}>
              <Text parts={item} />
            </li>
          ))}
        </ul>
      );
    case 'table':
      return (
        <table className="mt-3 w-full max-w-3xl text-sm">
          <thead className="text-left text-stone-500">
            <tr>
              {block.header.map((heading) => (
                <th key={heading} className="py-1 pr-3 font-normal">
                  {heading}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {block.rows.map((row, rowIndex) => (
              <tr key={rowIndex} className="border-t border-stone-100">
                {row.map((cell, cellIndex) => (
                  <td key={cellIndex} className="py-1 pr-3 align-top">
                    <Text parts={cell} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      );
    case 'headline':
      return <Headline />;
    case 'pages':
      return <Pages current={current} />;
  }
};

/**
 * Every page in reading order with the current one marked. A column beside the
 * text from tablet width, a wrapping list above it on a phone; the same list
 * either way, so a reader always sees where this page sits among the ten.
 */
const DocNav = ({ current }: { current: string }) => (
  <nav aria-label="Documentation" className="mb-6 md:mb-0 md:sticky md:top-4 md:self-start" data-testid="doc-nav">
    <p className="text-xs font-semibold uppercase tracking-wide text-stone-500">Documentation</p>
    <ol className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm md:block md:space-y-1.5">
      {docPages.map((page) => (
        <li key={page.slug}>
          <Link to={`/about/${page.slug}`} current={page.slug === current} className={page.slug === current ? 'font-semibold text-stone-900' : 'text-teal-800 hover:underline decoration-teal-300 underline-offset-2'}>
            {page.title}
          </Link>
        </li>
      ))}
    </ol>
  </nav>
);

export const Doc = ({ page }: { page: DocPageContent }) => {
  usePageTitle(pageTitle(page.title));
  // A hint's link carries a fragment; the application routes without a reload, so the scroll is done here.
  useEffect(() => {
    const id = location.hash.slice(1);
    if (id.length > 0) {
      document.getElementById(id)?.scrollIntoView();
    }
  }, [page.slug]);
  const next = nextPage(page.slug);
  const previous = previousPage(page.slug);
  return (
    <div className="md:grid md:grid-cols-[13rem_minmax(0,1fr)] md:gap-10">
      <DocNav current={page.slug} />
      <article className="min-w-0">
        <h1 className="text-2xl font-semibold tracking-tight">{page.title}</h1>
        <p className="mt-2 max-w-3xl text-stone-700">{page.lead}</p>
        <nav aria-label="Sections" className="mt-4 flex flex-wrap gap-x-4 gap-y-1 text-sm" data-testid="doc-contents">
          {page.sections.map((section) => (
            <a key={section.id} href={`#${section.id}`} className="text-teal-800 hover:underline">
              {section.heading}
            </a>
          ))}
        </nav>
        {page.sections.map((section) => (
          <section key={section.id} id={section.id} className="mt-8 scroll-mt-4">
            <h2 className="border-b border-stone-200 pb-1 text-base font-semibold text-stone-800">{section.heading}</h2>
            {section.blocks.map((block, index) => (
              <BlockView key={index} block={block} current={page.slug} />
            ))}
          </section>
        ))}
        <nav aria-label="Previous and next" className="mt-10 flex flex-wrap justify-between gap-4 border-t border-stone-200 pt-4 text-sm text-stone-600" data-testid="doc-pager">
          <span>{previous === undefined ? null : <>Previous: <Link to={`/about/${previous.slug}`}>{previous.title}</Link></>}</span>
          <span>{next === undefined ? null : <>Next: <Link to={`/about/${next.slug}`}>{next.title}</Link></>}</span>
        </nav>
      </article>
    </div>
  );
};
